import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./myServices.css";
import CreateServiceModal from "../../Service/CreateServiceModal";
import EditServiceModal from "../../Service/EditServiceModal";
import AddListingModal from "./AddListingModal";
import { REST_API_URL } from "../../api/config";
import {
  FiTool,
  FiHome,
  FiClock,
  FiPlus,
  FiChevronRight,
} from "react-icons/fi";
import { CiMoneyBill, CiClock2 } from "react-icons/ci";
import toast from "react-hot-toast";

const BASE_URL = REST_API_URL;

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const getCurrencySymbol = (code) => {
  return CURRENCY_SYMBOL_MAP[code] || code || "$";
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  return num % 1 === 0
    ? num.toLocaleString()
    : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatDuration = (minutes) => {
  const total = Number(minutes);
  if (!total && total !== 0) return "—";
  if (Number.isNaN(total)) return "—";

  const h = Math.floor(total / 60);
  const m = total % 60;

  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const getListingPriceLabel = (listing) => {
  const type = String(listing?.listing_type || "").toLowerCase();

  if (type === "sell" || type === "sale") {
    return `Sale: ${formatMoney(listing?.sell_price)}`;
  }

  if (type === "rent") {
    if (listing?.monthly_rent_price) {
      return `Rent: ${formatMoney(listing.monthly_rent_price)}/mo`;
    }
    if (listing?.yearly_rent_price) {
      return `Rent: ${formatMoney(listing.yearly_rent_price)}/yr`;
    }
  }

  return `Price: ${formatMoney(
    listing?.sell_price ||
      listing?.monthly_rent_price ||
      listing?.yearly_rent_price,
  )}`;
};

function SegmentedTabs({ activeTab, onChange, isRealEstate }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: 6,
        borderRadius: 18,
        background: "#f3f4f6",
        border: "1px solid #e5e7eb",
        position: "relative",
      }}
    >
      <button
        type="button"
        onClick={() => onChange("services")}
        style={{
          border: "none",
          background: activeTab === "services" ? "#ffffff" : "transparent",
          color: activeTab === "services" ? "#111827" : "#6b7280",
          padding: "10px 16px",
          borderRadius: 14,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow:
            activeTab === "services" ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
          transition: "all 0.2s ease",
        }}
      >
        Services
      </button>

      {isRealEstate && (
        <button
          type="button"
          onClick={() => onChange("listings")}
          style={{
            border: "none",
            background: activeTab === "listings" ? "#ffffff" : "transparent",
            color: activeTab === "listings" ? "#111827" : "#6b7280",
            padding: "10px 16px",
            borderRadius: 14,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow:
              activeTab === "listings"
                ? "0 1px 2px rgba(16,24,40,0.06)"
                : "none",
            transition: "all 0.2s ease",
          }}
        >
          Listings
        </button>
      )}
    </div>
  );
}

function EmptyState({ icon, title, description }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        border: "1px dashed #d1d5db",
        background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
        borderRadius: 24,
        padding: 32,
        minHeight: 280,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div
          style={{
            width: 72,
            height: 72,
            margin: "0 auto 18px",
            borderRadius: 22,
            background: "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#4b5563",
          }}
        >
          {icon}
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 22,
            lineHeight: 1.2,
            fontWeight: 700,
            color: "#111827",
          }}
        >
          {title}
        </h3>
        <p
          style={{
            marginTop: 10,
            marginBottom: 0,
            color: "#6b7280",
            fontSize: 15,
            lineHeight: 1.6,
          }}
        >
          {description}
        </p>
      </div>
    </motion.div>
  );
}

function StatPill({ icon, label, value }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 16,
        background: "#f9fafb",
        border: "1px solid #eef2f7",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          background: "#ffffff",
          border: "1px solid #eef2f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4f46e5",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ service, onEdit, currencySymbol }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        padding: 20,
        boxShadow: "0 6px 20px rgba(16,24,40,0.04)",
        display: "flex",
        flexDirection: "column",
        minHeight: 240,
        height: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          minHeight: 96,
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 16,
            background: "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#4338ca",
            flexShrink: 0,
          }}
        >
          <FiTool size={22} />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                lineHeight: 1.25,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {service.name}
            </h3>

            {service.isFromSuggested && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#4338ca",
                  background: "#eef2ff",
                  padding: "6px 10px",
                  borderRadius: 999,
                }}
              >
                Suggested
              </span>
            )}

            {service.isLocal && !service.isFromSuggested && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#92400e",
                  background: "#fffbeb",
                  padding: "6px 10px",
                  borderRadius: 999,
                }}
              >
                Local
              </span>
            )}
          </div>

          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.6,
              color: "#6b7280",
            }}
          >
            {service.description?.length > 135
              ? `${service.description.slice(0, 135)}...`
              : service.description || "No description"}
          </p>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 10,
          marginTop: "auto",
        }}
      >
        <StatPill
          icon={<CiMoneyBill size={20} />}
          label="Price"
          value={`${currencySymbol}${formatMoney(service.price)}`}
        />
        <StatPill
          icon={<CiClock2 size={18} />}
          label="Duration"
          value={formatDuration(service.duration)}
        />
      </div>

      <div style={{ marginTop: "20px" }}>
        <button
          type="button"
          onClick={() => onEdit(service)}
          style={{
            width: "100%",
            border: "none",
            borderRadius: 16,
            padding: "13px 16px",
            background: "#111827",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          Edit Service
        </button>
      </div>
    </motion.div>
  );
}

function ListingCard({ listing }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        padding: 20,
        boxShadow: "0 6px 20px rgba(16,24,40,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minHeight: 240,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          minHeight: 96,
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 16,
            background: "linear-gradient(135deg, #ecfeff 0%, #eff6ff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#0f766e",
            flexShrink: 0,
          }}
        >
          <FiHome size={22} />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                lineHeight: 1.25,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {listing.property_name || "Unnamed property"}
            </h3>

            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#0f766e",
                background: "#ecfeff",
                padding: "6px 10px",
                borderRadius: 999,
              }}
            >
              {listing.listing_type || "Listing"}
            </span>
          </div>

          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.6,
              color: "#6b7280",
            }}
          >
            {listing.property_description?.length > 135
              ? `${listing.property_description.slice(0, 135)}...`
              : listing.property_description || "No description"}
          </p>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 10,
          marginTop: "auto",
        }}
      >
        <StatPill
          icon={<CiMoneyBill size={20} />}
          label="Pricing"
          value={getListingPriceLabel(listing)}
        />
        <StatPill
          icon={<CiClock2 size={18} />}
          label="Tour duration"
          value={formatDuration(listing.tour_duration)}
        />
      </div>
    </motion.div>
  );
}

export default function MyServices() {
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [listings, setListings] = useState([]);
  const [error, setError] = useState("");
  const [listingError, setListingError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingListings, setLoadingListings] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showListingModal, setShowListingModal] = useState(false);
  const [editService, setEditService] = useState(null);
  const [isRealEstate, setIsRealEstate] = useState(false);
  const [activeTab, setActiveTab] = useState("services");

  const isFetched = useState(false)[0];

  const [currencies, setCurrencies] = useState([]);
  const [userCurrency, setUserCurrency] = useState(
    localStorage.getItem("user_currency") || "USD",
  );

  const loadCurrencies = async () => {
    try {
      const token = await getValidToken();
      if (!token) throw new Error("Session expired");

      const headers = buildHeaders(token);

      const res = await fetch(`${BASE_URL}/service/currencies/`, {
        method: "GET",
        headers,
      });

      if (!res.ok) throw new Error("Failed to load currencies");

      const data = await res.json().catch(() => []);
      setCurrencies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load currencies:", err);
      setCurrencies([]);
    }
  };
  useEffect(() => {
    const timeZone = getUserTimeZone();
    const userStatus = localStorage.getItem("user_status") || "individual";
    const code = userStatus === "salon" ? "228" : "216";

    localStorage.setItem("user_timezone", timeZone);
    localStorage.setItem("onboarding_code", code);

    sessionStorage.setItem("onboarding_force_route", "/my-services");
    sessionStorage.setItem("onboarding_force_status", userStatus);
  }, []);

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "";

    if (!token) return null;

    const headers = buildHeaders(null);

    const verify = await fetch(`${BASE_URL}/auth/token/verify/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ token }),
    });

    if (verify.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");

    if (!refresh) return null;

    const res = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ refresh }),
    });

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.access) {
      const useLocalStorage =
        !!localStorage.getItem("accessToken") ||
        !!localStorage.getItem("refreshToken");

      if (useLocalStorage) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      setError("");

      const token = await getValidToken();
      if (!token) throw new Error("Session expired");

      const headers = buildHeaders(token);

      const res = await fetch(`${BASE_URL}/service/my-services/`, {
        method: "GET",
        headers,
      });

      if (!res.ok) throw new Error("Failed to load services");

      const data = await res.json().catch(() => []);
      setServices(
        Array.isArray(data)
          ? data.map((service) => ({ ...service, isLocal: false }))
          : [],
      );
    } catch (err) {
      setError(err.message || "Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const loadListings = async () => {
    try {
      setLoadingListings(true);
      setListingError("");

      const token = await getValidToken();
      if (!token) throw new Error("Session expired");

      const headers = buildHeaders(token);

      const res = await fetch(`${BASE_URL}/service/real-estate-my-listings/`, {
        method: "GET",
        headers,
      });

      if (!res.ok) throw new Error("Failed to load listings");

      const data = await res.json().catch(() => []);
      const normalized = Array.isArray(data)
        ? data
        : data?.results || data?.data || [];

      setListings(normalized);
    } catch (err) {
      setListingError(err.message || "Failed to load listings");
      setListings([]);
    } finally {
      setLoadingListings(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("selectedServicesPayload");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const wasClosed = localStorage.getItem("my_services_closed");
    if (wasClosed === "true") {
      localStorage.removeItem("my_services_closed");
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    const initPage = async () => {
      let realEstateMode = false;

      try {
        const token = await getValidToken();
        if (token) {
          const headers = buildHeaders(token);

          const res = await fetch(`${BASE_URL}/auth/get-industry/`, {
            method: "GET",
            headers,
          });

          const data = await res.json().catch(() => ({}));

          if (data?.industry === "Sales & Real Estate") {
            realEstateMode = true;
            setIsRealEstate(true);
            localStorage.setItem("real_estate_mode", "true");
          } else {
            setIsRealEstate(false);
            localStorage.removeItem("real_estate_mode");
          }
        }
      } catch {
        setIsRealEstate(false);
        localStorage.removeItem("real_estate_mode");
      }

      await loadCurrencies();
      await loadServices();

      const payload = localStorage.getItem("selectedServicesPayload");

      if (payload && !wasClosed) {
        try {
          const parsed = JSON.parse(payload);

          const converted = parsed.map((srv) => ({
            id: srv.id || Date.now() + Math.random(),
            name: srv.name,
            description: srv.description,
            duration: parseInt(srv.duration, 10),
            price: parseFloat(srv.price),
            service_count: srv.service_count || "1",
            time_unit: "minutes",
            on_off: false,
            isLocal: true,
            isFromSuggested: true,
            image_url: srv.image_url || null,
          }));

          setServices((prev) => {
            const existingIds = new Set(prev.map((s) => s.id));
            const newServices = converted.filter((s) => !existingIds.has(s.id));
            return [...prev, ...newServices];
          });
        } catch {}
      }

      if (realEstateMode) {
        await loadListings();
      }
    };

    initPage();

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFetched]);

  const createAllLocalServices = async () => {
    const token = await getValidToken();
    if (!token) throw new Error("Session expired");

    const servicesToCreate = services.filter((service) => service.isLocal);
    if (servicesToCreate.length === 0) return [];

    const headers = buildHeaders(token);

    const response = await fetch(`${BASE_URL}/service/service-create/`, {
      method: "POST",
      headers,
      body: JSON.stringify(
        servicesToCreate.map((service) => ({
          name: service.name,
          description: service.description,
          duration: Number(service.duration),
          time_unit: "minutes",
          price: parseFloat(service.price),
          currency: "USD",
          service_count: service.service_count,
          on_off: false,
          image_url: service.image_url || null,
          timeZone: getUserTimeZone(),
        })),
      ),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || "Failed to create services");
    }

    localStorage.removeItem("selectedServicesPayload");

    return await response.json().catch(() => []);
  };

  const handleNext = async () => {
    if (services.length === 0) {
      toast.error("Please create at least one service before continuing.");
      return;
    }

    setLoading(true);

    try {
      await createAllLocalServices();

      const userStatus = localStorage.getItem("user_status") || "individual";
      const nextCode = userStatus === "salon" ? "230" : "218";

      localStorage.setItem("onboarding_code", nextCode);
      localStorage.setItem("onboarding_step_completed", "true");

      sessionStorage.setItem("onboarding_force_route", "/working-days");
      sessionStorage.setItem("onboarding_force_status", userStatus);

      navigate("/working-days", { replace: true });
    } catch (error) {
      toast.error(`Error saving services: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addNewService = (newService) => {
    const serviceWithId = {
      ...newService,
      id: Date.now(),
      price: parseFloat(newService.price),
      duration: parseInt(newService.duration, 10),
      service_count: newService.service_count,
      isLocal: true,
      isFromSuggested: false,
    };

    setServices((prev) => [...prev, serviceWithId]);
    toast.success("Service added locally");
  };

  const updateService = (updatedService) => {
    setServices((prev) =>
      prev.map((service) =>
        service.id === updatedService.id ? updatedService : service,
      ),
    );
  };

  const removeService = (id) => {
    setServices((prev) => prev.filter((service) => service.id !== id));
    toast.success("Service removed");
  };

  const handleListingCreated = async () => {
    toast.success("Listing created");
    setShowListingModal(false);
    await loadListings();
    setActiveTab("listings");
  };

  const totalCount = services.length + listings.length;
  const localCount = services.filter((item) => item.isLocal).length;

  const showServiceContent = activeTab === "services" || !isRealEstate;
  const showListingContent = activeTab === "listings" && isRealEstate;

  return (
    <motion.div
      className="my-service-page"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1180,
          margin: "0 auto",
          padding: "12px 0 24px",
        }}
      >
        <div
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%)",
            border: "1px solid #e5e7eb",
            borderRadius: 28,
            boxShadow: "0 16px 50px rgba(16,24,40,0.06)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "26px 26px 20px",
              borderBottom: "1px solid #eef2f7",
              background:
                "radial-gradient(circle at top left, rgba(99,102,241,0.08), transparent 32%), #ffffff",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 18,
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 30,
                    lineHeight: 1.1,
                    fontWeight: 500,
                    color: "#111827",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Create Your Services
                </h1>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                  minWidth: 320,
                  flex: "0 0 auto",
                }}
              >
                <StatPill
                  icon={<FiTool size={16} />}
                  label="Services"
                  value={services.length}
                />
                <StatPill
                  icon={<FiHome size={16} />}
                  label="Listings"
                  value={listings.length}
                />
              </div>
            </div>

            <div
              style={{
                marginTop: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <SegmentedTabs
                activeTab={activeTab}
                onChange={setActiveTab}
                isRealEstate={isRealEstate}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  disabled={loading}
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#ffffff",
                    color: "#111827",
                    borderRadius: 16,
                    padding: "12px 16px",
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FiPlus size={16} />
                  Create Service
                </button>

                {isRealEstate && (
                  <button
                    type="button"
                    onClick={() => setShowListingModal(true)}
                    disabled={loadingListings}
                    style={{
                      border: "none",
                      background:
                        "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
                      color: "#ffffff",
                      borderRadius: 16,
                      padding: "12px 16px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      boxShadow: "0 10px 25px rgba(17,24,39,0.18)",
                    }}
                  >
                    <FiPlus size={16} />
                    Add Listing
                  </button>
                )}
              </div>
            </div>
          </div>

          <div style={{ padding: 26 }}>
            {showServiceContent && (
              <>
                {loading ? (
                  <div
                    style={{
                      minHeight: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6b7280",
                      fontWeight: 600,
                    }}
                  >
                    Loading services...
                  </div>
                ) : error ? (
                  <div
                    style={{
                      border: "1px solid #fecaca",
                      background: "#fef2f2",
                      color: "#b91c1c",
                      borderRadius: 18,
                      padding: 16,
                      fontWeight: 600,
                    }}
                  >
                    {error}
                  </div>
                ) : services.length === 0 ? (
                  <EmptyState
                    icon={<FiTool size={30} />}
                    title="No services yet"
                    description="Create your first service to continue with onboarding. Keep the list focused and add only what you really offer."
                  />
                ) : (
                  <motion.div
                    layout
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 16,
                    }}
                  >
                    <AnimatePresence>
                      {services.map((service) => (
                        <ServiceCard
                          key={service.id}
                          service={service}
                          onEdit={setEditService}
                          currencySymbol={getCurrencySymbol(userCurrency)}
                        />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )}
              </>
            )}

            {showListingContent && (
              <>
                {loadingListings ? (
                  <div
                    style={{
                      minHeight: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6b7280",
                      fontWeight: 600,
                    }}
                  >
                    Loading listings...
                  </div>
                ) : listingError ? (
                  <div
                    style={{
                      border: "1px solid #fecaca",
                      background: "#fef2f2",
                      color: "#b91c1c",
                      borderRadius: 18,
                      padding: 16,
                      fontWeight: 600,
                    }}
                  >
                    {listingError}
                  </div>
                ) : listings.length === 0 ? (
                  <EmptyState
                    icon={<FiHome size={30} />}
                    title="No listings yet"
                    description="Add your first listing to show available properties. You can still continue onboarding based on your services."
                  />
                ) : (
                  <motion.div
                    layout
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 16,
                    }}
                  >
                    <AnimatePresence>
                      {listings.map((listing) => (
                        <ListingCard key={listing.id} listing={listing} />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )}
              </>
            )}
          </div>

          <div
            style={{
              borderTop: "1px solid #eef2f7",
              padding: "18px 26px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              background: "#fcfcfd",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#111827",
                  fontWeight: 700,
                }}
              ></div>
            </div>

            <button
              type="button"
              className={`${services.length === 0 ? "disabled-btn" : ""}`}
              onClick={handleNext}
              disabled={loading || services.length === 0}
              style={{
                border: "none",
                borderRadius: 18,
                padding: "14px 20px",
                background:
                  services.length === 0
                    ? "#d1d5db"
                    : "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                color: "#ffffff",
                fontWeight: 800,
                cursor: services.length === 0 ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                boxShadow:
                  services.length === 0
                    ? "none"
                    : "0 14px 30px rgba(79,70,229,0.24)",
              }}
            >
              {loading ? "Saving to Server..." : "Save & Continue"}
              {!loading && <FiChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateServiceModal
          onClose={() => setShowCreateModal(false)}
          onServiceCreated={addNewService}
        />
      )}

      {showListingModal && (
        <AddListingModal
          onClose={() => setShowListingModal(false)}
          onCreated={handleListingCreated}
        />
      )}

      {editService && (
        <EditServiceModal
          service={editService}
          onClose={() => setEditService(null)}
          onServiceUpdated={updateService}
          onServiceDeleted={removeService}
        />
      )}
    </motion.div>
  );
}
